import type { UserSession } from "@airange/shared/auth";
import {
  requestAdminBffAuthorize,
  requestAdminBffBootstrap,
  requestAdminBffCallback,
  requestAdminBffLogout,
} from "./admin-bff-auth-api";
import {
  createAdminRuntimeConfigError,
  resolveAdminRuntimeAccessConfig,
  sanitizeAdminRuntimeRedirectPath,
} from "./admin-runtime-access-config";
import {
  clearSession,
  clearStoredAccessToken,
  setStoredAuthSource,
  setStoredUser,
} from "./session";
import { sanitizeRedirectPath } from "./sso-redirect";

export type AdminBffCallbackCompleteResult = {
  redirectPath: string;
  user: UserSession;
};

export type StartAdminBffAuthorizationOptions = {
  prompt?: "none" | "login";
};

export { OIDC_CALLBACK_PATH } from "@airange/shared/oidc-routes";

const BFF_PENDING_REDIRECT_PATH_KEY = "airange.admin.bff.pending_redirect_path";

function hasOwn(record: Record<string, unknown>, key: string) {
  return Object.prototype.hasOwnProperty.call(record, key);
}

function readAdminBffBootstrapUser(
  payload: Awaited<ReturnType<typeof requestAdminBffBootstrap>>,
): UserSession | null {
  const record = payload as Record<string, unknown>;
  if (!hasOwn(record, "user")) {
    throw createAdminRuntimeConfigError(
      "admin.access.bff_bootstrap_user_missing",
      "Admin BFF bootstrap response must include user; use null only for anonymous sessions.",
    );
  }
  if (payload.user === null) {
    return null;
  }
  return payload.user;
}

function storeAdminBffUser(user: UserSession) {
  clearStoredAccessToken();
  setStoredUser(user);
  setStoredAuthSource("sso");
}

function requireAdminBffCallbackRedirectPath(value: unknown) {
  if (typeof value !== "string" || !value.trim()) {
    throw createAdminRuntimeConfigError(
      "admin.access.bff_callback_redirect_path_missing",
      "Admin BFF callback response must include redirect_path.",
    );
  }
  return value;
}

export function consumeAdminBffAuthorizationRedirectPath(defaultPath = "/dashboard") {
  if (typeof window === "undefined") return defaultPath;
  const value = window.sessionStorage.getItem(BFF_PENDING_REDIRECT_PATH_KEY);
  window.sessionStorage.removeItem(BFF_PENDING_REDIRECT_PATH_KEY);
  return sanitizeRedirectPath(value, defaultPath);
}

export async function startAdminBffAuthorization(
  redirectPath: string,
  options: StartAdminBffAuthorizationOptions = {},
) {
  if (typeof window === "undefined") {
    return;
  }
  const accessConfig = await resolveAdminRuntimeAccessConfig();
  const nextRedirectPath = sanitizeAdminRuntimeRedirectPath(
    accessConfig,
    redirectPath || accessConfig.defaultRedirectPath,
  );
  window.sessionStorage.setItem(BFF_PENDING_REDIRECT_PATH_KEY, nextRedirectPath);
  const { authorization_url: authorizationUrl } = await requestAdminBffAuthorize(
    nextRedirectPath,
    options.prompt,
  );
  window.location.assign(authorizationUrl);
}

export async function completeAdminBffCallback(
  _code: string,
  _state: string,
  fetchImpl: typeof fetch = fetch,
): Promise<AdminBffCallbackCompleteResult> {
  const accessConfig = await resolveAdminRuntimeAccessConfig(fetchImpl);
  const payload = await requestAdminBffCallback(_code, _state, fetchImpl);
  if (!payload.user) {
    throw createAdminRuntimeConfigError(
      "admin.access.bff_callback_user_missing",
      "Admin BFF callback response must include user.",
    );
  }
  storeAdminBffUser(payload.user);
  return {
    redirectPath: sanitizeAdminRuntimeRedirectPath(
      accessConfig,
      requireAdminBffCallbackRedirectPath(payload.redirect_path),
    ),
    user: payload.user,
  };
}

export async function bootstrapAdminBffSession(
  fetchImpl: typeof fetch = fetch,
): Promise<UserSession | null> {
  const payload = await requestAdminBffBootstrap(fetchImpl);
  const user = readAdminBffBootstrapUser(payload);
  if (!user) {
    clearSession();
    return null;
  }
  storeAdminBffUser(user);
  return user;
}

export async function logout(fetchImpl: typeof fetch = fetch) {
  try {
    await requestAdminBffLogout(fetchImpl);
  } finally {
    clearSession();
  }
}
